import { escapeHtml } from "./render-template";

// =============================================================================
// RÉFÉRENCE UNIQUE des emails AON — Bilan Retraite Individuel.
//
// Design aligné sur l'email d'invitation salarié (template `invitation_salarie`)
// qui fait foi : header sombre, rouge de marque AON `#EB0017`, titres Georgia
// serif, conteneur 600px, footer centré. TOUS les emails transactionnels du
// projet (invitation, relance, RDV, messages…) doivent passer par ces briques
// pour garantir une charte cohérente.
//
// Style 100% inline (compatibilité maximale clients mail).
// =============================================================================

/** Palette & typographies de référence (charte email AON). */
export const EMAIL_BRAND = {
  bg: "#f4f4f6",
  surface: "#ffffff",
  ink: "#1a1a1a",
  inkSoft: "#3a3a3a",
  muted: "#6b6b76",
  border: "#e5e5ea",
  brand: "#EB0017",
  fontSans: "Arial,Helvetica,sans-serif",
  fontSerif: "Georgia,serif",
} as const;

export interface EmailLayoutOptions {
  /** Texte de pré-en-tête (aperçu masqué dans la boîte de réception). */
  preheader?: string;
  /** Libellé du header (par défaut : « Aon — Bilan Retraite Individuel »). */
  headerLabel?: string;
}

/**
 * Enveloppe un fragment HTML (`content`) dans la charte email AON.
 * Accepte soit une string `preheader`, soit un objet d'options.
 */
export function emailLayout(content: string, options?: string | EmailLayoutOptions): string {
  const opts: EmailLayoutOptions =
    typeof options === "string" ? { preheader: options } : (options ?? {});
  const preheader = opts.preheader;
  const headerLabel = opts.headerLabel ?? "Aon — Bilan Retraite Individuel";
  const B = EMAIL_BRAND;

  return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <meta http-equiv="X-UA-Compatible" content="IE=edge" />
  <title>Aon — Bilan Retraite Individuel</title>
</head>
<body style="margin:0;padding:0;background-color:${B.bg};font-family:${B.fontSans};color:${B.ink};">
  ${preheader ? `<div style="display:none;font-size:1px;line-height:1px;max-height:0;max-width:0;opacity:0;overflow:hidden;">${escapeHtml(preheader)}</div>` : ""}
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="${B.bg}">
    <tr>
      <td align="center" style="padding:32px 16px;">
        <table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;width:100%;background:${B.surface};border-radius:12px;overflow:hidden;border:1px solid ${B.border};">
          <tr>
            <td bgcolor="${B.ink}" style="background-color:${B.ink};padding:24px 32px;color:#ffffff;font-size:14px;letter-spacing:.04em;text-transform:uppercase;font-weight:600;">
              ${escapeHtml(headerLabel)}
            </td>
          </tr>
          <tr>
            <td style="padding:32px;">
              ${content}
            </td>
          </tr>
          <tr>
            <td bgcolor="${B.bg}" style="background-color:${B.bg};padding:16px 32px;font-size:12px;line-height:18px;color:${B.muted};text-align:center;">
              © Aon Retraite — Plateforme BRI
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

// -----------------------------------------------------------------------------
// Briques de contenu réutilisables (à composer dans le `content`)
// -----------------------------------------------------------------------------

/** Titre principal (Georgia serif, comme l'invitation). `text` doit être échappé/sûr. */
export function emailHeading(text: string): string {
  return `<h1 style="margin:0 0 16px 0;font-family:${EMAIL_BRAND.fontSerif};font-size:26px;line-height:34px;color:${EMAIL_BRAND.ink};font-weight:600;">${text}</h1>`;
}

/** Paragraphe standard. `html` est inséré tel quel (peut contenir <strong>, <br/>…). */
export function emailParagraph(html: string): string {
  return `<p style="margin:0 0 16px 0;font-size:15px;line-height:24px;color:${EMAIL_BRAND.inkSoft};">${html}</p>`;
}

/** Bouton d'action principal (CTA rouge AON), construit en table pour Outlook. */
export function emailButton(href: string, label: string): string {
  return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 24px 0;">
  <tr>
    <td bgcolor="${EMAIL_BRAND.brand}" style="background-color:${EMAIL_BRAND.brand};border-radius:8px;">
      <a href="${escapeHtml(href)}" style="display:inline-block;padding:14px 28px;font-size:15px;font-weight:600;color:#ffffff;text-decoration:none;">${label}</a>
    </td>
  </tr>
</table>`;
}

/**
 * Encart d'informations (clé/valeur) — table grise arrondie.
 * `rows` : tableau de paires [label, valeur HTML] déjà sûres.
 */
export function emailInfoBox(rows: Array<[string, string]>): string {
  const body = rows
    .map(
      ([label, value]) =>
        `<tr><td style="padding:6px 0;font-size:14px;color:${EMAIL_BRAND.inkSoft};"><strong style="color:${EMAIL_BRAND.ink};">${label} :</strong> ${value}</td></tr>`,
    )
    .join("");
  return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:${EMAIL_BRAND.bg};border-radius:8px;padding:16px 20px;margin:0 0 24px 0;">${body}</table>`;
}

/** Séparateur + mention discrète (ex. confidentialité). `text` est inséré tel quel. */
export function emailDisclaimer(text: string): string {
  return `<hr style="margin:24px 0;border:none;border-top:1px solid ${EMAIL_BRAND.border};" />
<p style="margin:0;font-size:12px;line-height:18px;color:${EMAIL_BRAND.muted};">${text}</p>`;
}

/** Mention de confidentialité standard AON (secret professionnel). */
export const EMAIL_CONFIDENTIALITY_NOTE =
  "Aon est tenu au secret professionnel. Aucune information n'est transmise à votre employeur sans votre accord.";
